import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import useCountDown from 'react-countdown-hook';

import instapayLogo from './../../../../../statics/img/instapay/instapay_icon.png';
import paymobLogo from './../../../../../statics/img/esaypaisa/paymob.png';
import ModalView from './ModalView';
import Loader from './Loader';
import QRMethod from './QRMethod';
import CollectMethod from './CollectMethod';
import './Instapay.css';
import { HEADERS_CONFIG, INSTAPAY_API } from '../../../../tools/APIs';
import { getErrorMsg } from '../../../../../utils/getErrorMsg';
import CancelModal from './CancelModal';
import { useMobileDetection } from '../../../../../utils/useMobileDetection';
import RequestPayModal from './RequestPay/Modal';
import IntentMethod from './IntentMethod';

export const PAYMENT_METHODS = {
  COLLECT: 'collect',
  QR: 'qrcode',
  INTENT: 'intent',
};

function Instapay({ payment_token, initialPaymentData }) {
  const [timeLeft, { start }] = useCountDown();
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState(
    PAYMENT_METHODS.COLLECT
  );
  const [error, setError] = useState('');
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRequestPay, setIsRequestPay] = useState(false);
  const [isInquiring, setIsInquiring] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const countDownStartedRef = useRef(null);
  const isMobile = useMobileDetection();

  useEffect(
    () => {
      if (isMobile && (!isRequestPay && currentPaymentMethod !== PAYMENT_METHODS.QR)) {
        handlePay(PAYMENT_METHODS.INTENT);
      }
    },
    [isMobile]
  );

  useEffect(
    () => {
      if (!paymentData || countDownStartedRef.current) return;
      if (paymentData.data && paymentData.data.validity) {
        countDownStartedRef.current = true;
        start(paymentData.data.validity * 1000);
      }
    },
    [paymentData]
  );

  useEffect(
    () => {
      if (!countDownStartedRef.current) return;
      if (timeLeft <= 0) handleStartForceClosure();
    },
    [timeLeft]
  );

  async function handlePay(paymentMethod, inputValue) {
    setError('');
    setIsPaymentLoading(true);

    try {
      let identifier = inputValue;
      if (
        paymentMethod === PAYMENT_METHODS.QR ||
        paymentMethod === PAYMENT_METHODS.INTENT
      ) {
        identifier = PAYMENT_METHODS.QR;
      }
      const data = {
        source: {
          identifier: identifier,
          is_qrcode:
            paymentMethod === PAYMENT_METHODS.QR ||
            paymentMethod === PAYMENT_METHODS.INTENT,
          subtype: 'INSTAPAY',
        },
        payment_token: payment_token,
      };
      const jsonData = JSON.stringify(data);
      const response = await axios.post(
        INSTAPAY_API.PAY,
        jsonData,
        HEADERS_CONFIG
      );
      setPaymentData(response.data);

      paymentMethod === PAYMENT_METHODS.QR
        ? setCurrentPaymentMethod(PAYMENT_METHODS.QR)
        : setIsRequestPay(true);

      if (paymentMethod === PAYMENT_METHODS.INTENT) {
        setCurrentPaymentMethod(PAYMENT_METHODS.INTENT);
        window.location.href = response.data.qr_code;
      }
    } catch (error) {
      setError(getErrorMsg(error));
    } finally {
      setIsPaymentLoading(false);
    }
  }

  const handleInquiry = async () => {
    setError('');
    setIsCheckingStatus(true);
    setIsLoading(true);

    try {
      const data = {
        transaction: paymentData.transaction_id,
        payment_token: payment_token,
      };
      const jsonData = JSON.stringify(data);
      const response = await axios.post(
        INSTAPAY_API.INQUIRY,
        jsonData,
        HEADERS_CONFIG
      );

      if (response.data.pending) {
        setIsInquiring(true);
        setTimeout(() => setIsInquiring(false), 4000);
      } else {
        handleRedirection(response.data);
      }
    } catch (error) {
      setError(getErrorMsg(error));
    } finally {
      setIsCheckingStatus(false);
      setIsLoading(false);
    }
  };

  const handleStartForceClosure = () => {
    if (!(isCheckingStatus || isCanceling)) {
      handleForceClosure();
    } else {
      const intervalId = setInterval(() => {
        if (!(isCheckingStatus || isCanceling)) {
          handleForceClosure();
          clearInterval(intervalId);
        }
      }, 500);
    }
  };

  const handleForceClosure = async () => {
    setError('');
    setIsLoading(true);

    try {
      const data = {
        transaction: paymentData.transaction_id,
        payment_token: payment_token,
      };
      const jsonData = JSON.stringify(data);
      const response = await axios.post(
        INSTAPAY_API.FORCE_CLOSURE,
        jsonData,
        HEADERS_CONFIG
      );
      handleRedirection(response.data);
    } catch (error) {
      setError(getErrorMsg(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelPayment = async () => {
    setError('');
    setIsCanceling(true);
    setIsLoading(true);

    try {
      const data = {
        transaction: paymentData.transaction_id,
        payment_token: payment_token,
      };
      const jsonData = JSON.stringify(data);
      const response = await axios.post(
        INSTAPAY_API.CANCEL,
        jsonData,
        HEADERS_CONFIG
      );
      handleRedirection(response.data);
    } catch (error) {
      const errorData = error.response && error.response.data;
      handleRedirection(errorData);
    } finally {
      setIsCanceling(false);
      setIsLoading(false);
    }
  };

  const handleOpenCancelModal = () => setOpenCancelModal(true);

  const handleCloseCancelModal = () => setOpenCancelModal(false);

  const handleRedirection = (data) => {
    if (data && data.redirection_url) {
      window.location.href = data.redirection_url;
    }
  };

  const isIntentMethod = currentPaymentMethod === PAYMENT_METHODS.INTENT;
  const isCollectMethod = currentPaymentMethod === PAYMENT_METHODS.COLLECT;
  const isQRMethod = currentPaymentMethod === PAYMENT_METHODS.QR;

  return (
    <div className="instapay_page">
      {isPaymentLoading && (
        <ModalView>
          <Loader text="Loading..." />
        </ModalView>
      )}
      {openCancelModal && (
        <ModalView>
          <CancelModal
            onCancel={handleCancelPayment}
            isLoading={isLoading}
            onClose={handleCloseCancelModal}
          />
        </ModalView>
      )}
      {isIntentMethod && isRequestPay && (
        <ModalView>
          <RequestPayModal />
        </ModalView>
      )}

      <div className="instapay_container">
        <div className="instapay_subheader">
          <div className="instapay_subheader_content">
            <img
              className="instapay_subheader_content_img"
              src={instapayLogo}
              alt="instapay"
            />
            <p className="instapay_subheader_content_title">Instapay</p>
          </div>
          <div className="instapay_subheader_content_end">
            <p>
              Order Amount - EGP {initialPaymentData.order_amount_cents / 100}
            </p>
          </div>
        </div>
        {error && (
          <div className="instapay_error">
            <p className="instapay_error_content">{error}</p>
          </div>
        )}
        {isIntentMethod && <IntentMethod onPay={handlePay} />}

        {isCollectMethod && (
          <CollectMethod
            onPay={handlePay}
            isRequestPay={isRequestPay}
            isInquiring={isInquiring}
            onInquiry={handleInquiry}
            timer={timeLeft}
            isLoading={isPaymentLoading || isLoading}
            onOpenCancelModal={handleOpenCancelModal}
            paymentData={paymentData}
          >
            <div className="instapay_divider">
              <span className="instapay_divider_content">OR</span>
            </div>
            <button
              onClick={() => handlePay(PAYMENT_METHODS.QR)}
              className="instapay_btn instapay_btn_secondary"
              type="button"
            >
              Generate QR Code
            </button>
          </CollectMethod>
        )}

        {isQRMethod && (
          <QRMethod
            isInquiring={isInquiring}
            onInquiry={handleInquiry}
            timer={timeLeft}
            isLoading={isLoading}
            onOpenCancelModal={handleOpenCancelModal}
            paymentData={paymentData}
          />
        )}
        <div className="instapay_footer">
          <span>Powered by</span>
          <img src={paymobLogo} alt="Paymob" />
        </div>
      </div>
    </div>
  );
}

export default Instapay;
